import { useQuery } from "@tanstack/react-query"
import { toDictionary } from "@tokenterminal/core/utils/toDictionary"
import { useMemo } from "react"
import { useTRPCClient } from "utils/trpc"
import { useAvailableEcosystemMetricIds } from "./useAvailableEcosystemMetricIds"
import { useKeyMetricIds } from "./useKeyMetricIds"
import { useMetricsConfigurationList } from "./useMetricsConfigurationList"

const useKeyMetricBreakdown = ({
  dataId,
  metricIds,
  interval = "24h",
  productId,
}: {
  dataId: string
  metricIds: string[]
  interval?: "max" | "ytd" | "24h" | "7d" | "30d" | "90d" | "180d" | "365d"
  productId?: string
}) => {
  const trpcClient = useTRPCClient()
  return useQuery({
    queryKey: ["getMetricsBreakdown", dataId, metricIds, interval, productId],
    queryFn: async () => {
      const result = await trpcClient.metrics.postBreakdown.mutate({
        data_ids: [dataId],
        metric_ids: metricIds,
        interval,
        groupBy: "projects",
        // Perhaps we should make this type optional?
        includeSelf: false,
        product_ids: productId ? [productId] : undefined,
      })

      const dataForDataId = result.data.find((item) => item.data_id === dataId)

      if (!dataForDataId) {
        return {}
      }

      return dataForDataId.metrics
    },
    staleTime: 1000 * 60 * 60 * 24, // 1 day
    enabled: !!dataId && !!metricIds.length,
  })
}

const useKeyMetricsWithEcosystemIds = ({
  projectSlug,
  dataId,
  marketSectorId,
  productId,
}: {
  projectSlug: string
  dataId: string
  marketSectorId: string
  productId?: string
}) => {
  const { data: keyMetricIds } = useKeyMetricIds({
    projectSlug,
    dataId,
    marketSectorId,
    productId,
  })
  const { data: ecosystemMetricIds = [] } = useAvailableEcosystemMetricIds({
    dataId,
  })
  // We're only considering gdp for now
  const filteredEcosystemMetricIds: string[] = ecosystemMetricIds.filter(
    (metricId) => metricId === "gdp"
  )
  return [...keyMetricIds, ...filteredEcosystemMetricIds]
}

type KeyMetric = {
  metricFormat: string | undefined
  metric_id: string
  prefix: string
  label: string
  value: number
  change: number
  slug: string
}

/**
 * Returns a list of key metrics for a project including a value and change percentage.
 * - For static metrics, we use the most recent average for the value and the 30d change for the change.
 * - For non-static metrics, we use the 30d average for the value and the 30d trend for the change.
 */

const useKeyMetrics = ({
  dataId,
  projectSlug,
  marketSectorId,
  productId,
}: {
  dataId: string
  projectSlug: string
  marketSectorId: string
  productId?: string
}) => {
  // Metric configurations
  const {
    data: metricConfigurations = [],
    isLoading: isLoadingMetricConfigurations,
  } = useMetricsConfigurationList()
  const metricConfigurationsById = toDictionary(
    metricConfigurations,
    "metric_id"
  )

  // Key ids
  const keyMetricIds = useKeyMetricsWithEcosystemIds({
    projectSlug,
    dataId,
    marketSectorId,
    productId,
  }).slice(0, 5)

  // Fetch aggregations
  const { data: keyMetricBreakdownById = {}, isLoading } =
    useKeyMetricBreakdown({
      dataId,
      metricIds: keyMetricIds,
      interval: "24h",
      productId,
    })
  const {
    data: keyMetricBreakdownByIdThirtyDay = {},
    isLoading: isLoadingThirtyDay,
  } = useKeyMetricBreakdown({
    dataId,
    metricIds: keyMetricIds,
    interval: "30d",
    productId,
  })

  // Combine aggregations with configurations and treat static and non-static metrics differently
  const keyMetricAggregations = useMemo(
    () =>
      keyMetricIds
        .map<KeyMetric | undefined>((metricId) => {
          const metricConfiguration = metricConfigurationsById.get(metricId)
          const twentyFourHourData = keyMetricBreakdownById[metricId]
          const thirtyDayData = keyMetricBreakdownByIdThirtyDay[metricId]
          if (!metricConfiguration || !twentyFourHourData || !thirtyDayData) {
            return undefined
          }

          const isStatic = metricConfiguration.static

          if (isStatic) {
            return {
              metric_id: metricId,
              label: metricConfiguration.title,
              value: twentyFourHourData.latest || twentyFourHourData.avg,
              change: thirtyDayData.change,
              slug: metricConfiguration.slug,
              prefix: metricConfiguration.format === "price" ? "$" : "",
              metricFormat: metricConfiguration.format,
            }
          } else {
            return {
              metric_id: metricId,
              label: metricConfiguration.title + " (30d)",
              value: thirtyDayData.sum || 0,
              change: thirtyDayData.trend,
              slug: metricConfiguration.slug,
              prefix: metricConfiguration.format === "price" ? "$" : "",
              metricFormat: metricConfiguration.format,
            }
          }
        })
        .filter(Boolean) as KeyMetric[],
    [
      keyMetricIds,
      metricConfigurationsById,
      keyMetricBreakdownById,
      keyMetricBreakdownByIdThirtyDay,
    ]
  )

  return {
    data: keyMetricAggregations,
    isLoading: isLoadingMetricConfigurations || isLoading || isLoadingThirtyDay,
  }
}

export default useKeyMetrics
